import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Footer } from './shared/ServicePageSections';
import ArrowIcon from '../components/ArrowIcon';
import { findProject, adjacentProjects } from '../data/projectsData';

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

function useRevealOnce(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const hexA = (hex, a) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/* Section wrapper with a shared reveal + eyebrow header */
function Section({ eyebrow, title, accent, color, children, background, style }) {
  const [ref, visible] = useRevealOnce(0.08);
  return (
    <section style={{ padding: 'clamp(46px,5.5vw,80px) clamp(20px,5vw,48px)', background, ...style }}>
      <div
        ref={ref}
        style={{
          maxWidth: 1180, margin: '0 auto',
          opacity: visible ? 1 : 0,
          transform: visible ? 'none' : 'translateY(26px)',
          transition: 'opacity 660ms cubic-bezier(.2,.8,.2,1), transform 660ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        {(eyebrow || title) && (
          <div style={{ marginBottom: 'clamp(24px,3vw,40px)' }}>
            {eyebrow && (
              <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: color || 'var(--blue)' }}>
                {eyebrow}
              </span>
            )}
            {title && (
              <h2 style={{
                fontFamily: "'Anton',sans-serif", fontWeight: 400,
                fontSize: 'clamp(26px,3.6vw,46px)', lineHeight: 1.06, color: 'var(--ink)', marginTop: 10,
              }}>
                {title}
                {accent && (
                  <>{' '}<span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: color || 'var(--red)' }}>{accent}</span></>
                )}
              </h2>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   ARCHITECTURE DIAGRAM
   Layers flow top → bottom with a live spine between them.
═══════════════════════════════════════════════════════════ */

function ArchitectureFlow({ layers, color }) {
  return (
    <div style={{ position: 'relative' }}>
      {layers.map((l, i) => (
        <div key={l.layer} style={{ position: 'relative', paddingBottom: i < layers.length - 1 ? 18 : 0 }}>
          <div
            className="pd-arch-row"
            style={{
              display: 'grid', gridTemplateColumns: '168px 1fr', gap: 'clamp(16px,2.4vw,32px)',
              alignItems: 'center', background: 'white', border: '1px solid var(--line)',
              borderRadius: 16, padding: 'clamp(16px,1.8vw,22px) clamp(16px,2vw,24px)',
              boxShadow: '0 4px 18px rgba(10,10,20,0.05)', position: 'relative', overflow: 'hidden',
            }}
          >
            <div aria-hidden style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
              background: `linear-gradient(180deg, ${color}, ${hexA(color, 0.15)})`,
            }} />

            {/* layer label */}
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.14em',
                color, marginBottom: 5,
              }}>
                {String(i + 1).padStart(2, '0')} · {l.layer}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.3 }}>
                {l.title}
              </div>
            </div>

            {/* nodes */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>
              {l.nodes.map((n, ni) => (
                <span
                  key={n}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    fontSize: 12.5, fontWeight: 500, color: 'var(--ink-soft)',
                    background: hexA(color, 0.05), border: `1px solid ${hexA(color, 0.18)}`,
                    borderRadius: 9, padding: '8px 13px',
                  }}
                >
                  <span style={{
                    width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0,
                    animation: `redPulse ${2.4 + ((i + ni) % 4) * 0.3}s ease-in-out ${(i * 0.2 + ni * 0.15)}s infinite`,
                  }} />
                  {n}
                </span>
              ))}
            </div>
          </div>

          {/* connector */}
          {i < layers.length - 1 && (
            <div aria-hidden style={{
              position: 'absolute', left: 'clamp(34px,4vw,52px)', bottom: 0, height: 18,
              width: 2, background: hexA(color, 0.28),
            }}>
              <div style={{
                position: 'absolute', left: -3, top: 0, width: 8, height: 8, borderRadius: '50%',
                background: color, animation: `pdFlow 2.6s ease-in-out ${i * 0.4}s infinite`,
              }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NARRATIVE BLOCK (Challenge / Approach / Outcome)
═══════════════════════════════════════════════════════════ */

function NarrativeBlock({ n, label, body, points, color, tone }) {
  const [ref, visible] = useRevealOnce(0.1);
  const isDark = tone === 'dark';
  return (
    <div
      ref={ref}
      style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'clamp(16px,2.4vw,30px)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: 'opacity 660ms cubic-bezier(.2,.8,.2,1), transform 660ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      {/* number rail */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Anton',sans-serif", fontSize: 18, color: 'white',
          background: color, boxShadow: `0 8px 22px ${hexA(color, 0.35)}`,
        }}>{n}</div>
        <div style={{ flex: 1, width: 1, background: `linear-gradient(180deg, ${hexA(color, 0.3)}, transparent)`, minHeight: 20 }} />
      </div>

      {/* content */}
      <div style={{ paddingBottom: 'clamp(28px,3.4vw,46px)', minWidth: 0 }}>
        <h3 style={{
          fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(21px,2.4vw,30px)',
          lineHeight: 1.14, color: isDark ? 'white' : 'var(--ink)', marginBottom: 12,
        }}>{label}</h3>

        <p style={{
          fontSize: 'clamp(14px,1.3vw,16px)', lineHeight: 1.86,
          color: isDark ? 'rgba(255,255,255,0.66)' : 'var(--ink-soft)', marginBottom: points?.length ? 20 : 0,
        }}>{body}</p>

        {points && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {points.map(pt => (
              <div key={pt} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 4 }}>
                  <circle cx="8" cy="8" r="7" stroke={hexA(color, 0.45)} strokeWidth="1.2" />
                  <path d="M5 8.2l2 2L11 6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{
                  fontSize: 'clamp(13px,1.2vw,14.5px)', lineHeight: 1.7,
                  color: isDark ? 'rgba(255,255,255,0.72)' : 'var(--ink-soft)',
                }}>{pt}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NOT FOUND
═══════════════════════════════════════════════════════════ */

function ProjectNotFound() {
  return (
    <>
      <Helmet><title>Project not found — Zone-X</title></Helmet>
      <section style={{ paddingTop: 180, paddingBottom: 120, textAlign: 'center', padding: '180px clamp(20px,5vw,48px) 120px' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: 'clamp(48px,9vw,110px)', color: 'var(--ink)', opacity: 0.08, lineHeight: 1 }}>404</div>
        <h1 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(26px,4vw,44px)', color: 'var(--ink)', marginTop: -18 }}>
          That case study{' '}
          <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'var(--red)' }}>isn&rsquo;t here</span>
        </h1>
        <p style={{ fontSize: 15, color: 'var(--ink-soft)', marginTop: 14, marginBottom: 30 }}>
          The project you followed may have been renamed or retired.
        </p>
        <Link to="/projects" className="hoverable" style={{
          display: 'inline-flex', alignItems: 'center', gap: 10, padding: '15px 28px',
          background: 'var(--ink)', color: 'white', borderRadius: 999, fontSize: 14, fontWeight: 700,
        }}>
          View all projects <ArrowIcon size={13} />
        </Link>
      </section>
      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const p = findProject(slug);
  const [heroRef, heroVisible] = useRevealOnce(0.02);

  if (!p) return <ProjectNotFound />;

  const { prev, next } = adjacentProjects(slug);
  const c = p.color;

  const META = [
    { k: 'Client',   v: p.client },
    { k: 'Scale',    v: p.clientNote },
    { k: 'Sector',   v: p.sectorName },
    { k: 'Year',     v: p.year },
    { k: 'Duration', v: p.duration },
    { k: 'Team',     v: p.team },
  ];

  return (
    <>
      <Helmet>
        <title>{`${p.title} — Zone-X Case Study`}</title>
        <meta name="description" content={p.summary} />
        <link rel="canonical" href={`https://zone-x.in/projects/${p.slug}`} />
        <meta property="og:title" content={`${p.title} — Zone-X Case Study`} />
        <meta property="og:description" content={p.summary} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* ══════════════ HERO ══════════════ */}
      <section
        ref={heroRef}
        style={{
          position: 'relative', paddingTop: 108, overflow: 'hidden',
          background: 'linear-gradient(165deg, #0A0A14 0%, #131327 58%, #0A0A14 100%)',
        }}
      >
        {/* background image */}
        <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <img
            src={p.image}
            alt=""
            style={{
              width: '100%', height: '100%', objectFit: 'cover', opacity: 0.34,
              animation: 'pdHeroDrift 22s ease-in-out infinite alternate',
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(180deg, rgba(10,10,20,0.72) 0%, ${hexA(c, 0.18)} 45%, rgba(10,10,20,0.94) 100%)`,
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px)',
            backgroundSize: '34px 34px',
          }} />
        </div>

        <div style={{
          position: 'relative', zIndex: 1, maxWidth: 1180, margin: '0 auto',
          padding: '0 clamp(20px,5vw,48px) clamp(44px,5vw,72px)',
        }}>
          {/* breadcrumb */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
            fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 'clamp(28px,4vw,52px)',
          }}>
            <Link to="/" className="hoverable" style={{ color: 'rgba(255,255,255,0.45)' }}>Home</Link>
            <span>/</span>
            <Link to="/projects" className="hoverable" style={{ color: 'rgba(255,255,255,0.45)' }}>Projects</Link>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.8)' }}>{p.title}</span>
          </div>

          <div style={{
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'none' : 'translateY(28px)',
            transition: 'opacity 800ms cubic-bezier(.2,.8,.2,1), transform 800ms cubic-bezier(.2,.8,.2,1)',
          }}>
            {/* chips */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 22 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'white', background: c, padding: '5px 12px', borderRadius: 5,
              }}>{p.sectorName}</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'rgba(255,255,255,0.66)',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)',
                padding: '4.5px 11px', borderRadius: 5,
              }}>{p.code}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#4ADE80' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22C55E', animation: 'redPulse 2.4s ease-in-out infinite' }} />
                Deployed {p.year}
              </span>
            </div>

            <h1 style={{
              fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'white',
              fontSize: 'clamp(34px,6vw,80px)', lineHeight: 0.98, margin: 0, maxWidth: 900,
            }}>{p.title}</h1>

            <p style={{
              fontFamily: "'Instrument Serif', serif", fontStyle: 'italic',
              fontSize: 'clamp(17px,2.2vw,28px)', color: hexA('#FFFFFF', 0.72),
              marginTop: 16, maxWidth: 700, lineHeight: 1.35,
            }}>{p.tagline}</p>

            <p style={{
              fontSize: 'clamp(14px,1.3vw,16.5px)', lineHeight: 1.84,
              color: 'rgba(255,255,255,0.6)', marginTop: 22, maxWidth: 720,
            }}>{p.summary}</p>

            {/* services */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 28 }}>
              {p.services.map(s => (
                <span key={s} style={{
                  fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.74)',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 999, padding: '7px 15px',
                }}>{s}</span>
              ))}
            </div>
          </div>
        </div>

        {/* metric strip riding the hero base */}
        <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.09)' }}>
          <div className="pd-metric-strip" style={{
            maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: `repeat(${p.metrics.length},1fr)`,
          }}>
            {p.metrics.map((m, i) => (
              <div key={m.label} style={{
                padding: 'clamp(18px,2.2vw,28px) clamp(14px,2vw,24px)',
                borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.09)',
              }}>
                <div style={{
                  fontFamily: "'Anton',sans-serif", fontWeight: 400, lineHeight: 1,
                  fontSize: 'clamp(22px,3vw,38px)',
                  color: i === 0 ? '#FFFFFF' : 'rgba(255,255,255,0.9)',
                  ...(i === 0 ? {
                    background: `linear-gradient(120deg, #FFFFFF, ${c})`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  } : {}),
                }}>{m.value}</div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginTop: 8 }}>{m.label}</div>
                <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.32)', marginTop: 2 }}>{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ AT A GLANCE ══════════════ */}
      <Section eyebrow="AT A GLANCE" color={c}>
        <div className="pd-meta-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 1,
          background: 'var(--line)', border: '1px solid var(--line)', borderRadius: 16, overflow: 'hidden',
        }}>
          {META.map(m => (
            <div key={m.k} style={{ background: 'white', padding: 'clamp(14px,1.7vw,20px)' }}>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: hexA(c, 0.85), marginBottom: 7,
              }}>{m.k}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 }}>{m.v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ══════════════ CHALLENGE ══════════════ */}
      <Section
        eyebrow="THE BRIEF"
        title="Where it"
        accent="started"
        color={c}
        background="var(--paper)"
      >
        <NarrativeBlock
          n="01"
          label="The challenge"
          body={p.challenge.body}
          points={p.challenge.points}
          color={c}
        />
      </Section>

      {/* ══════════════ APPROACH ══════════════ */}
      <Section eyebrow="ENGINEERING" title="How we" accent="built it" color={c}>
        <div style={{ marginBottom: 'clamp(24px,3vw,38px)' }}>
          <p style={{ fontSize: 'clamp(14.5px,1.4vw,17px)', lineHeight: 1.86, color: 'var(--ink-soft)', maxWidth: 780 }}>
            {p.approach.body}
          </p>
        </div>

        <div className="pd-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 'clamp(12px,1.6vw,20px)' }}>
          {p.approach.steps.map((s, i) => <ApproachCard key={s.title} s={s} i={i} color={c} />)}
        </div>
      </Section>

      {/* ══════════════ ARCHITECTURE ══════════════ */}
      <Section
        eyebrow="SYSTEM DESIGN"
        title="The architecture"
        accent="we shipped"
        color={c}
        background="var(--paper)"
      >
        <ArchitectureFlow layers={p.architecture} color={c} />

        {/* stack */}
        <div className="pd-stack" style={{
          display: 'grid', gridTemplateColumns: `repeat(${p.stack.length},1fr)`,
          gap: 'clamp(12px,1.6vw,20px)', marginTop: 'clamp(24px,3vw,38px)',
        }}>
          {p.stack.map(g => (
            <div key={g.group} style={{
              background: 'white', border: '1px solid var(--line)', borderRadius: 16,
              padding: 'clamp(16px,2vw,22px)',
            }}>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: c, marginBottom: 14,
                paddingBottom: 10, borderBottom: `1px solid ${hexA(c, 0.16)}`,
              }}>{g.group}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {g.items.map(it => (
                  <span key={it} style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--ink-soft)',
                    background: 'rgba(10,10,20,0.035)', border: '1px solid rgba(10,10,20,0.07)',
                    borderRadius: 5, padding: '4px 9px',
                  }}>{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ══════════════ TIMELINE ══════════════ */}
      <Section eyebrow="DELIVERY" title="How it" accent="ran" color={c}>
        <div className="pd-timeline" style={{
          display: 'grid', gridTemplateColumns: `repeat(${p.timeline.length},1fr)`, gap: 0,
        }}>
          {p.timeline.map((t, i) => <TimelinePhase key={t.phase} t={t} i={i} last={i === p.timeline.length - 1} color={c} />)}
        </div>
      </Section>

      {/* ══════════════ OUTCOME ══════════════ */}
      <section style={{
        background: 'linear-gradient(150deg, #0A0A14 0%, #14142A 55%, #0A0A14 100%)',
        padding: 'clamp(48px,6vw,84px) clamp(20px,5vw,48px)', position: 'relative', overflow: 'hidden',
      }}>
        <div aria-hidden style={{
          position: 'absolute', top: '-35%', right: '2%', width: '48%', height: '130%',
          background: `radial-gradient(ellipse, ${hexA(c, 0.28)} 0%, transparent 62%)`,
        }} />
        <div aria-hidden style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />

        <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: 'clamp(24px,3vw,40px)' }}>
            <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
              THE RESULT
            </span>
            <h2 style={{
              fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'white',
              fontSize: 'clamp(26px,3.6vw,46px)', lineHeight: 1.06, marginTop: 10,
            }}>
              What changed{' '}
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#FF6B72' }}>once it was live</span>
            </h2>
          </div>

          <NarrativeBlock
            n="02"
            label="The outcome"
            body={p.outcome.body}
            points={p.outcome.points}
            color={c}
            tone="dark"
          />

          {/* quote */}
          {p.quote && (
            <blockquote style={{
              marginTop: 'clamp(12px,2vw,24px)', padding: 'clamp(24px,3vw,36px)',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20, borderLeft: `3px solid ${c}`,
            }}>
              <svg width="26" height="20" viewBox="0 0 26 20" fill="none" style={{ marginBottom: 14, opacity: 0.4 }}>
                <path d="M0 20V11.6C0 5.2 3.5 1 10 0l1 2.8C7.2 4 5.4 6 5.4 8.6H10V20H0zm15 0v-8.4C15 5.2 18.5 1 25 0l1 2.8C22.2 4 20.4 6 20.4 8.6H25V20H15z" fill="white" />
              </svg>
              <p style={{
                fontFamily: "'Instrument Serif', serif", fontStyle: 'italic',
                fontSize: 'clamp(17px,2vw,25px)', lineHeight: 1.5, color: 'rgba(255,255,255,0.9)', marginBottom: 18,
              }}>
                &ldquo;{p.quote.text}&rdquo;
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 2, background: c }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>{p.quote.author}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.42)', marginTop: 1 }}>{p.quote.role}</div>
                </div>
              </div>
            </blockquote>
          )}
        </div>
      </section>

      {/* ══════════════ GALLERY ══════════════ */}
      {p.gallery?.length > 1 && (
        <Section eyebrow="IN THE FIELD" title="Where it" accent="lives" color={c}>
          <div className="pd-gallery" style={{
            display: 'grid', gridTemplateColumns: `repeat(${Math.min(p.gallery.length, 3)},1fr)`,
            gap: 'clamp(10px,1.4vw,16px)',
          }}>
            {p.gallery.map((g, i) => <GalleryTile key={`${g}-${i}`} src={g} alt={`${p.title} — view ${i + 1}`} color={c} i={i} />)}
          </div>
        </Section>
      )}

      {/* ══════════════ NEXT / PREV ══════════════ */}
      <section style={{ padding: 'clamp(20px,3vw,40px) clamp(20px,5vw,48px) clamp(48px,6vw,80px)' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingBottom: 18, marginBottom: 22, borderBottom: '1px solid var(--line)', flexWrap: 'wrap', gap: 12,
          }}>
            <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--blue)' }}>
              KEEP READING
            </span>
            <Link to="/projects" className="hoverable" style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--ink)',
            }}>
              All case studies <ArrowIcon size={11} />
            </Link>
          </div>

          <div className="pd-adjacent" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(12px,1.6vw,20px)' }}>
            {prev && <AdjacentCard p={prev} dir="Previous" />}
            {next && <AdjacentCard p={next} dir="Next" />}
          </div>
        </div>
      </section>

      {/* ══════════════ CTA ══════════════ */}
      <section style={{ padding: '0 clamp(20px,5vw,48px) clamp(56px,6vw,88px)' }}>
        <div style={{
          maxWidth: 1180, margin: '0 auto', borderRadius: 24, overflow: 'hidden', position: 'relative',
          background: `linear-gradient(135deg, ${hexA(c, 0.07)} 0%, rgba(0,60,255,0.05) 100%)`,
          border: `1px solid ${hexA(c, 0.18)}`, padding: 'clamp(32px,4vw,56px)',
        }}>
          <div className="pd-cta-inner" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 'clamp(20px,3vw,44px)', flexWrap: 'wrap',
          }}>
            <div style={{ maxWidth: 620 }}>
              <h2 style={{
                fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(24px,3.4vw,42px)',
                lineHeight: 1.08, color: 'var(--ink)',
              }}>
                Facing something{' '}
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: c }}>similar?</span>
              </h2>
              <p style={{ fontSize: 'clamp(13.5px,1.3vw,15.5px)', lineHeight: 1.8, color: 'var(--ink-soft)', marginTop: 14 }}>
                We&rsquo;ll walk your site, read your protocols, and tell you honestly what is worth building — before
                anybody signs anything.
              </p>
            </div>
            <Link
              to="/contact"
              className="hoverable"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap',
                padding: '17px 32px', borderRadius: 999, background: 'var(--ink)', color: 'white',
                fontSize: 14.5, fontWeight: 700, boxShadow: '0 14px 34px rgba(10,10,20,0.22)',
                transition: 'transform 220ms ease, background 220ms ease, box-shadow 220ms ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.background = c;
                e.currentTarget.style.boxShadow = `0 20px 44px ${hexA(c, 0.4)}`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'var(--ink)';
                e.currentTarget.style.boxShadow = '0 14px 34px rgba(10,10,20,0.22)';
              }}
            >
              Talk to an engineer <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes pdHeroDrift { from { transform: scale(1.04) translate(0,0); } to { transform: scale(1.12) translate(-1.5%, -1%); } }
        @keyframes pdFlow { 0% { top: -2px; opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { top: 14px; opacity: 0; } }

        @media (max-width: 1000px) {
          .pd-meta-grid   { grid-template-columns: repeat(3,1fr) !important; }
          .pd-timeline    { grid-template-columns: 1fr !important; }
          .pd-stack       { grid-template-columns: 1fr !important; }
          .pd-metric-strip{ grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 760px) {
          .pd-steps    { grid-template-columns: 1fr !important; }
          .pd-arch-row { grid-template-columns: 1fr !important; row-gap: 14px !important; }
          .pd-adjacent { grid-template-columns: 1fr !important; }
          .pd-gallery  { grid-template-columns: 1fr !important; }
          .pd-cta-inner{ flex-direction: column; align-items: flex-start; }
        }
        @media (max-width: 520px) {
          .pd-meta-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════════ */

function ApproachCard({ s, i, color }) {
  const [hov, setHov] = useState(false);
  const [ref, visible] = useRevealOnce(0.1);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'white', border: '1px solid var(--line)', borderRadius: 18,
        padding: 'clamp(20px,2.2vw,26px)', position: 'relative', overflow: 'hidden',
        boxShadow: hov ? `0 18px 44px rgba(10,10,20,0.1), 0 0 0 1px ${hexA(color, 0.3)}` : '0 3px 16px rgba(10,10,20,0.05)',
        transform: visible ? (hov ? 'translateY(-4px)' : 'translateY(0)') : 'translateY(24px)',
        opacity: visible ? 1 : 0,
        transition: `opacity 640ms cubic-bezier(.2,.8,.2,1) ${i * 90}ms, transform 400ms cubic-bezier(.2,.8,.2,1), box-shadow 340ms ease`,
      }}
    >
      {/* ghost index */}
      <div aria-hidden style={{
        position: 'absolute', top: -14, right: 6, fontFamily: "'Anton',sans-serif",
        fontSize: 88, lineHeight: 1, color: 'var(--ink)', opacity: hov ? 0.06 : 0.035,
        transition: 'opacity 340ms ease', userSelect: 'none', pointerEvents: 'none',
      }}>{String(i + 1).padStart(2, '0')}</div>

      <div style={{
        width: 30, height: 30, borderRadius: 9, marginBottom: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hexA(color, 0.1), border: `1px solid ${hexA(color, 0.24)}`,
      }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, color }}>
          {i + 1}
        </span>
      </div>

      <h4 style={{ fontSize: 'clamp(15px,1.5vw,17.5px)', fontWeight: 700, color: 'var(--ink)', marginBottom: 9, lineHeight: 1.3, position: 'relative' }}>
        {s.title}
      </h4>
      <p style={{ fontSize: 13.5, lineHeight: 1.74, color: 'var(--ink-soft)', position: 'relative' }}>
        {s.body}
      </p>
    </div>
  );
}

function TimelinePhase({ t, i, last, color }) {
  const [ref, visible] = useRevealOnce(0.15);
  return (
    <div
      ref={ref}
      style={{
        position: 'relative', paddingRight: last ? 0 : 'clamp(12px,1.6vw,22px)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(22px)',
        transition: `opacity 620ms cubic-bezier(.2,.8,.2,1) ${i * 110}ms, transform 620ms cubic-bezier(.2,.8,.2,1) ${i * 110}ms`,
      }}
    >
      {/* rail */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{
          width: 13, height: 13, borderRadius: '50%', flexShrink: 0,
          background: color, boxShadow: `0 0 0 4px ${hexA(color, 0.16)}`,
        }} />
        {!last && (
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, ${hexA(color, 0.4)}, ${hexA(color, 0.1)})` }} />
        )}
      </div>

      <div style={{ paddingRight: 'clamp(8px,1.4vw,20px)' }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.1em',
          color: hexA(color, 0.9), marginBottom: 6,
        }}>{t.weeks}</div>
        <div style={{ fontSize: 'clamp(15px,1.5vw,18px)', fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          {t.phase}
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-soft)' }}>{t.body}</p>
      </div>
    </div>
  );
}

function GalleryTile({ src, alt, color, i }) {
  const [hov, setHov] = useState(false);
  const [ref, visible] = useRevealOnce(0.1);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative', borderRadius: 18, overflow: 'hidden', background: '#0A0A14',
        height: 'clamp(200px,26vw,300px)', border: '1px solid var(--line)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: `opacity 660ms cubic-bezier(.2,.8,.2,1) ${i * 90}ms, transform 660ms cubic-bezier(.2,.8,.2,1) ${i * 90}ms`,
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: hov ? 'scale(1.06)' : 'scale(1)',
          opacity: hov ? 1 : 0.9,
          transition: 'transform 750ms cubic-bezier(.2,.8,.2,1), opacity 380ms ease',
        }}
      />
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(200deg, ${hexA(color, 0.2)} 0%, transparent 55%, rgba(10,10,20,0.4) 100%)`,
        opacity: hov ? 0.6 : 1, transition: 'opacity 380ms ease',
      }} />
    </div>
  );
}

function AdjacentCard({ p, dir }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={`/projects/${p.slug}`}
      className="hoverable"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        background: 'white', border: '1px solid var(--line)', borderRadius: 18,
        overflow: 'hidden', padding: 12,
        boxShadow: hov ? `0 18px 44px rgba(10,10,20,0.11), 0 0 0 1px ${hexA(p.color, 0.32)}` : '0 3px 16px rgba(10,10,20,0.05)',
        transform: hov ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'box-shadow 340ms ease, transform 340ms ease',
      }}
    >
      <div style={{ width: 92, height: 76, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: '#0A0A14', position: 'relative' }}>
        <img
          src={p.image}
          alt=""
          loading="lazy"
          style={{
            width: '100%', height: '100%', objectFit: 'cover', opacity: 0.88,
            transform: hov ? 'scale(1.08)' : 'scale(1)', transition: 'transform 620ms cubic-bezier(.2,.8,.2,1)',
          }}
        />
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, ${hexA(p.color, 0.24)}, rgba(10,10,20,0.34))` }} />
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'rgba(10,10,20,0.35)', marginBottom: 6,
        }}>{dir}</div>
        <div style={{
          fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(15px,1.6vw,19px)',
          lineHeight: 1.2, color: hov ? p.color : 'var(--ink)', transition: 'color 240ms ease',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{p.title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginTop: 4 }}>{p.sectorName} · {p.year}</div>
      </div>

      <span style={{
        display: 'inline-flex', color: hov ? p.color : 'rgba(10,10,20,0.3)', paddingRight: 8,
        transform: hov ? 'translateX(3px)' : 'translateX(0)',
        transition: 'transform 260ms ease, color 240ms ease',
      }}>
        <ArrowIcon size={16} />
      </span>
    </Link>
  );
}
